import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Space, Table, Tabs, Tag, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import * as api from '../lib/api'
import { resolveBusinessUnitLogoUrl } from './businessUnitTheme'
import { useI18n } from '../i18n/I18nProvider'

export default function BusinessUnitManager ({ onBusinessUnitsChanged, canManage = true }) {
  const { t } = useI18n()
  const [businessUnits, setBusinessUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const onBusinessUnitsChangedRef = useRef(onBusinessUnitsChanged)

  useEffect(() => {
    onBusinessUnitsChangedRef.current = onBusinessUnitsChanged
  }, [onBusinessUnitsChanged])

  const loadBusinessUnits = useCallback(async () => {
    setLoading(true)
    try {
      setBusinessUnits(await api.getAppraisalTemplates())
      if (onBusinessUnitsChangedRef.current) onBusinessUnitsChangedRef.current()
    } catch (e) {
      message.error(e.message || t('templates.messages.load_failed', 'Unable to load templates'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadBusinessUnits()
  }, [loadBusinessUnits])

  async function deleteBusinessUnit (id) {
    try {
      await api.deleteAppraisalTemplate(id)
      await loadBusinessUnits()
      message.success(t('templates.messages.deleted', 'Template deleted'))
    } catch (e) {
      message.error(e.message || t('templates.messages.delete_failed', 'Unable to delete template'))
    }
  }

  const columns = [
    { title: t('templates.table.template', 'Template'), dataIndex: 'name', key: 'name' },
    {
      title: t('templates.table.organization', 'Organization'),
      dataIndex: 'organization_name',
      key: 'organization_name',
      width: 120,
      render: value => value || '—'
    },
    {
      title: t('templates.table.regions', 'Regions'),
      dataIndex: 'region_names',
      key: 'regions',
      width: 180,
      render: names => (names || []).length ? names.map(name => <Tag key={name}>{name}</Tag>) : <Tag>{t('templates.unassigned', 'Unassigned')}</Tag>
    },
    {
      title: t('templates.table.branding', 'Branding'),
      key: 'branding',
      width: 140,
      render: (_, record) => (
        <Space size={6}>
          {record.logo_url && <img src={resolveBusinessUnitLogoUrl(record.logo_url)} alt='' style={{ height: 20, maxWidth: 44, objectFit: 'contain' }} />}
          <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 4, border: '1px solid #d9d9d9', background: record.primary_color }} />
          <span style={{ display: 'inline-block', width: 18, height: 18, borderRadius: 4, border: '1px solid #d9d9d9', background: record.secondary_color }} />
        </Space>
      )
    },
    {
      title: t('templates.table.status', 'Status'),
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: value => value ? <Tag color='green'>{t('common.active', 'Active')}</Tag> : <Tag>{t('common.inactive', 'Inactive')}</Tag>
    },
    canManage && {
      title: '',
      key: 'actions',
      width: 190,
      render: (_, record) => (
        <Space size='small'>
          <Link to={`/appraisal-templates/${record.id}/edit`}><Button size='small'>{t('common.edit', 'Edit')}</Button></Link>
          <Popconfirm
            title={t('templates.confirm.delete', 'Delete template?')}
            description={t('templates.confirm.delete_description', 'This also deletes its configured categories and questions.')}
            okButtonProps={{ danger: true, className: 'tti-btn-danger' }}
            onConfirm={() => deleteBusinessUnit(record.id)}
          >
            <Button size='small' className='tti-btn-danger'>{t('common.delete', 'Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ].filter(Boolean)

  const activeTemplates = businessUnits.filter(template => template.is_active)
  const inactiveTemplates = businessUnits.filter(template => !template.is_active)

  const renderTemplateTable = (rows) => (
    <Table
      dataSource={rows}
      columns={columns}
      rowKey='id'
      size='small'
      loading={loading}
      pagination={false}
    />
  )

  return (
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      <Card
        size='small'
        title={t('templates.title', 'Templates')}
        extra={canManage ? <Link to='/appraisal-templates/new'><Button size='small'>{t('templates.actions.create', 'Create Template')}</Button></Link> : null}
      >
        <Tabs
          items={[
            { key: 'active', label: t('templates.tabs.active', 'Active ({{count}})', { count: activeTemplates.length }), children: renderTemplateTable(activeTemplates) },
            { key: 'inactive', label: t('templates.tabs.inactive', 'Inactive ({{count}})', { count: inactiveTemplates.length }), children: renderTemplateTable(inactiveTemplates) }
          ]}
        />
      </Card>
    </Space>
  )
}
